import { useState, useEffect } from "react";
import { Moon, Sun, Monitor, Check, PanelLeft, PanelLeftClose, Palette, LayoutDashboard } from "lucide-react";
import { useTheme } from "../../components/themeProvider";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";

/* ----------------------------- theme primitives ---------------------------- */

const SettingCard = ({ icon: Icon, title, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                <Icon className="h-4.5 w-4.5" />
            </span>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        </div>
        {children}
    </div>
);

const OptionTile = ({ active, onClick, icon: Icon, title, description, preview }) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            "relative text-left w-full rounded-xl border p-3 transition-all duration-200",
            active
                ? "border-sidebar-primary ring-2 ring-sidebar-primary/30 bg-sidebar/5"
                : "border-gray-200 dark:border-gray-700 hover:border-sidebar-primary/50 bg-white dark:bg-gray-900"
        )}
    >
        {active && (
            <span className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-sidebar text-white grid place-items-center">
                <Check className="h-3 w-3" />
            </span>
        )}
        {preview}
        <div className="flex items-center gap-2 mt-2.5">
            <Icon className={cn("h-4 w-4", active ? "text-sidebar-primary" : "text-gray-400")} />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
        </div>
        {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>}
    </button>
);

const ThemePreview = ({ variant }) => {
    if (variant === "system") {
        return (
            <div className="h-16 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex">
                <div className="w-1/2 bg-white p-2 space-y-1.5">
                    <div className="h-1.5 w-7 rounded bg-sidebar" />
                    <div className="h-1 w-10 rounded bg-gray-200" />
                    <div className="h-1 w-8 rounded bg-gray-200" />
                </div>
                <div className="w-1/2 bg-gray-900 p-2 space-y-1.5">
                    <div className="h-1.5 w-7 rounded bg-sidebar" />
                    <div className="h-1 w-10 rounded bg-gray-700" />
                    <div className="h-1 w-8 rounded bg-gray-700" />
                </div>
            </div>
        );
    }
    const dark = variant === "dark";
    return (
        <div className={cn("h-16 rounded-lg border overflow-hidden p-2 space-y-1.5", dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200")}>
            <div className="h-1.5 w-7 rounded bg-sidebar" />
            <div className={cn("h-1 w-12 rounded", dark ? "bg-gray-700" : "bg-gray-200")} />
            <div className={cn("h-1 w-9 rounded", dark ? "bg-gray-700" : "bg-gray-200")} />
            <div className={cn("h-1 w-11 rounded", dark ? "bg-gray-700" : "bg-gray-200")} />
        </div>
    );
};

const SidebarPreview = ({ collapsed }) => (
    <div className="h-16 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex bg-white dark:bg-gray-900">
        <div className={cn("bg-sidebar p-1.5 space-y-1.5 transition-all", collapsed ? "w-5" : "w-12")}>
            <div className="h-1.5 w-full rounded bg-white/80" />
            {!collapsed && <div className="h-1 w-3/4 rounded bg-white/40" />}
            {!collapsed && <div className="h-1 w-2/3 rounded bg-white/40" />}
            {collapsed && <div className="h-1 w-full rounded bg-white/40" />}
        </div>
        <div className="flex-1 p-2 space-y-1.5">
            <div className="h-1.5 w-10 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-1 w-16 rounded bg-gray-100 dark:bg-gray-800" />
            <div className="h-1 w-12 rounded bg-gray-100 dark:bg-gray-800" />
        </div>
    </div>
);

/* --------------------------------- screen ---------------------------------- */

const Appearance = () => {
    const { theme, setTheme, mounted } = useTheme();
    const [selectedTheme, setSelectedTheme] = useState(theme);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    useEffect(() => {
        if (mounted) {
            const storedSidebarState = localStorage.getItem("sidebar_preference");
            if (storedSidebarState) {
                const isCollapsed = storedSidebarState === "collapsed";
                setSidebarCollapsed(isCollapsed);
                applySidebarPreference(isCollapsed);
            }

            const storedTheme = localStorage.getItem("theme-preference");
            if (storedTheme === "dark") {
                setTheme("dark");
                setSelectedTheme("dark");
                applyThemeToDocument("dark");
            } else {
                setSelectedTheme(theme);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mounted, theme, setTheme]);

    const handleThemeChange = (newTheme) => {
        setSelectedTheme(newTheme);
        setTheme(newTheme);
        localStorage.setItem("theme", newTheme);
        applyThemeToDocument(newTheme);
    };

    const handleSidebarChange = (newState) => {
        const isCollapsed = newState === "collapsed";
        setSidebarCollapsed(isCollapsed);
        localStorage.setItem("sidebar_preference", newState);
        applySidebarPreference(isCollapsed);
        window.dispatchEvent(new CustomEvent("sidebarPreferenceChange", {
            detail: { collapsed: isCollapsed },
        }));
    };

    const applyThemeToDocument = (themeToApply) => {
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");
        if (themeToApply === "system") {
            const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
            root.classList.add(systemTheme);
        } else {
            root.classList.add(themeToApply);
        }
    };

    const applySidebarPreference = (isCollapsed) => {
        const root = window.document.documentElement;
        root.classList.remove("sidebar-expanded", "sidebar-collapsed");
        root.classList.add(isCollapsed ? "sidebar-collapsed" : "sidebar-expanded");
        root.setAttribute("data-sidebar", isCollapsed ? "collapsed" : "expanded");
    };

    if (!mounted) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="h-56 bg-gray-200 dark:bg-gray-700 rounded-2xl" />
                    <div className="h-56 bg-gray-200 dark:bg-gray-700 rounded-2xl" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                    <Palette className="h-5 w-5" />
                </div>
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Appearance & Layout</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Customize how the application looks and behaves on your device. Changes apply instantly and are saved automatically.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Theme */}
                <SettingCard icon={Monitor} title="Theme">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <OptionTile
                            active={selectedTheme === "light"}
                            onClick={() => handleThemeChange("light")}
                            icon={Sun}
                            title="Light"
                            description="Best for daytime"
                            preview={<ThemePreview variant="light" />}
                        />
                        <OptionTile
                            active={selectedTheme === "dark"}
                            onClick={() => handleThemeChange("dark")}
                            icon={Moon}
                            title="Dark"
                            description="Easy on the eyes"
                            preview={<ThemePreview variant="dark" />}
                        />
                        <OptionTile
                            active={selectedTheme === "system"}
                            onClick={() => handleThemeChange("system")}
                            icon={Monitor}
                            title="System"
                            description="Match device"
                            preview={<ThemePreview variant="system" />}
                        />
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                        <Button
                            onClick={() => handleThemeChange(theme === "dark" ? "light" : "dark")}
                            variant="outline"
                            className="w-full flex items-center justify-center gap-2 border-sidebar/30 text-sidebar-primary hover:bg-sidebar/10"
                        >
                            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                            {theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        </Button>
                    </div>
                </SettingCard>

                {/* Sidebar */}
                <SettingCard icon={LayoutDashboard} title="Sidebar Layout">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <OptionTile
                            active={!sidebarCollapsed}
                            onClick={() => handleSidebarChange("expanded")}
                            icon={PanelLeft}
                            title="Expanded"
                            description="Full labels and icons"
                            preview={<SidebarPreview collapsed={false} />}
                        />
                        <OptionTile
                            active={sidebarCollapsed}
                            onClick={() => handleSidebarChange("collapsed")}
                            icon={PanelLeftClose}
                            title="Collapsed"
                            description="Icons only, more space"
                            preview={<SidebarPreview collapsed={true} />}
                        />
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3 py-2.5">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Toggle sidebar anywhere</span>
                            <span className="flex items-center gap-1">
                                <kbd className="px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 font-mono text-gray-700 dark:text-gray-300">Ctrl</kbd>
                                <span className="text-gray-400 text-xs">+</span>
                                <kbd className="px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 font-mono text-gray-700 dark:text-gray-300">B</kbd>
                            </span>
                        </div>
                    </div>
                </SettingCard>
            </div>

            {/* Tips */}
            <div className="rounded-2xl border border-sidebar/30 bg-sidebar/5 dark:bg-sidebar-primary/10 p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-lg bg-sidebar/15 text-sidebar-primary grid place-items-center">💡</span>
                    Pro Tips
                </h3>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1.5">
                    <li>• Your theme and sidebar preferences are saved automatically per device.</li>
                    <li>• Choose <strong>System</strong> to follow your OS theme automatically.</li>
                    <li>• Dark mode can help save battery on OLED screens.</li>
                    <li>• A collapsed sidebar gives you more horizontal workspace.</li>
                </ul>
            </div>
        </div>
    );
};

export default Appearance;
